'use client';

import { ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import type { MultichainExploitResult } from '@revoke.cash/core/exploits';
import { deduplicateArray } from '@revoke.cash/core/utils';
import Button from 'components/common/Button';
import { useTranslations } from 'next-intl';
import ExploitResultCard from './ExploitResultCard';

interface Props {
  status: 'affected' | 'previously_affected';
  results: MultichainExploitResult[];
  address: string;
}

const ExploitResultSection = ({ status, results, address }: Props) => {
  const t = useTranslations();

  const spenderAddresses = deduplicateArray(
    results.flatMap((result) => result.affectedSpenders.map((spender) => spender.address)),
  );
  const spenderSearch = spenderAddresses.join(',');
  const revokeHref = `/address/${address}?spenderSearch=${spenderSearch}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {status === 'affected' ? (
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
            ) : (
              <ShieldCheckIcon className="h-6 w-6 text-yellow-500" />
            )}
            <h2 className="text-xl font-bold">{t(`exploits.premium.${status}.title`)}</h2>
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">{t(`exploits.premium.${status}.description`)}</p>
        </div>
        {status === 'affected' && (
          <Button
            href={revokeHref}
            size="md"
            style="secondary"
            className="w-full sm:w-auto justify-center shrink-0"
            router
          >
            {t('exploits.premium.affected.button')}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {results.map((result) => (
          <ExploitResultCard key={result.exploit.slug} result={result} />
        ))}
      </div>
    </div>
  );
};

export default ExploitResultSection;
